(() => {
  "use strict";

  const SHARE_PREFIX = "#asv-te-planer=";
  const DEVICE_IDS = ["ls1", "ls3", "fi2", "fi4", "fils", "spd", "hs", "schuetz", "zeit", "klingel", "sonstige", "zaehler", "relais", "dinsteckdose", "blindabdeckung"];
  const field = (root, selector) => root.querySelector(selector);
  const escapeHtml = (value) => String(value).replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" }[character]));
  const euro = (cents) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(cents) / 100);
  const integer = (value) => Number.isInteger(Number(value)) ? Number(value) : NaN;
  const number = (value) => Number(String(value ?? "").replace(",", "."));

  function products(root) {
    try {
      const parsed = JSON.parse(root.dataset.teProdukte || "[]");
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((product) => product && product.verfuegbar === true && Number.isInteger(Number(product.te_gesamt)) && Number(product.te_gesamt) > 0 && Number.isInteger(Number(product.reihen)) && Number(product.reihen) > 0 && ["AP", "UP"].includes(product.montage) && /^IP[0-6][0-9X]$/u.test(product.ip || "") && /^\/products\//u.test(new URL(product.url, location.origin).pathname));
    } catch {
      return [];
    }
  }

  function collect(root) {
    const rows = [];
    for (const id of DEVICE_IDS) {
      const count = integer(field(root, `[data-te-anzahl="${id}"]`)?.value);
      const units = number(field(root, `[data-te-wert="${id}"]`)?.value);
      const title = field(root, `[data-te-geraet="${id}"] .asv-te-planer__name`)?.textContent?.trim() || id;
      if (!Number.isInteger(count) || count < 0) return { error: `${title}: Anzahl muss eine ganze Zahl ab 0 sein.` };
      if (count === 0) continue;
      if (!Number.isFinite(units) || units <= 0) return { error: `${title}: TE je Gerät aus dem Datenblatt eintragen.` };
      rows.push({ id, title, count, units, total: count * units });
    }
    const reserve = number(field(root, "[data-te-reserve]")?.value);
    const perRow = integer(field(root, "[data-te-pro-reihe]")?.value);
    if (!rows.length) return { error: "Mindestens ein Gerät mit Anzahl und TE-Angabe eintragen." };
    if (!Number.isFinite(reserve) || reserve < 0 || reserve > 100) return { error: "Die Platzreserve muss zwischen 0 und 100 % liegen." };
    if (!Number.isInteger(perRow) || perRow < 1) return { error: "TE je Reihe muss eine positive ganze Zahl sein." };
    const used = rows.reduce((sum, row) => sum + row.total, 0);
    const withReserve = Math.ceil(used * (1 + reserve / 100));
    const rowsRequired = Math.ceil(withReserve / perRow);
    return { rows, reserve, perRow, used, withReserve, rowsRequired, places: rowsRequired * perRow, montage: field(root, "[data-te-montage]")?.value || "egal", ip: field(root, "[data-te-ip]")?.value || "egal" };
  }

  function candidates(root, result) {
    return products(root).filter((product) => product.te_gesamt >= result.withReserve && product.reihen >= result.rowsRequired && (result.montage === "egal" || product.montage === result.montage) && (result.ip === "egal" || product.ip === result.ip)).sort((left, right) => Number(left.te_gesamt) - Number(right.te_gesamt) || Number(left.reihen) - Number(right.reihen) || Number(left.preis) - Number(right.preis) || String(left.id).localeCompare(String(right.id))).slice(0, 3);
  }

  function show(root, result) {
    const output = field(root, "[data-te-ergebnis]");
    if (result.error) {
      output.hidden = false;
      output.innerHTML = `<p class="asv-te-planer__warnung">${escapeHtml(result.error)}</p>`;
      return;
    }
    const choices = candidates(root, result);
    const details = result.rows.map((row) => `<div><dt>${escapeHtml(row.title)}</dt><dd>${row.count} × ${row.units.toLocaleString("de-DE")} TE = ${row.total.toLocaleString("de-DE")} TE</dd></div>`).join("");
    const rowLabel = result.rowsRequired === 1 ? "Reihe" : "Reihen";
    const cards = choices.length ? `<section class="asv-te-planer__vorschlaege"><h2>Kaufbare Gehäuse mit passender Kapazität</h2><p>Ab ${result.withReserve.toLocaleString("de-DE")} TE Bedarfswert nach der nächstgrößeren belegten Kapazität sortiert. Geräteverteilung und nutzbare Breite je Reihe vor dem Kauf prüfen.</p><ol>${choices.map((product) => `<li><a href="${escapeHtml(product.url)}">${escapeHtml(product.produkt)} — ${escapeHtml(product.titel)}</a><span>${Number(product.te_gesamt)} TE · ${Number(product.reihen)} Reihen · ${escapeHtml(product.montage)} · ${escapeHtml(product.ip)} · ${euro(product.preis)}</span></li>`).join("")}</ol></section>` : `<p class="asv-te-planer__warnung">Für diese Größe und die gewählten exakten Filter ist keine kaufbare Variante mit vollständigen Angaben zu Größe, Reihen, Montage und Schutzart hinterlegt.</p>`;
    output.hidden = false;
    output.innerHTML = `<div class="asv-te-planer__treffer"><strong>Benötigter Platz einschließlich ${result.reserve.toLocaleString("de-DE")} % Reserve: ${result.withReserve.toLocaleString("de-DE")} TE</strong><span>Bei ${result.perRow} TE je Reihe: ${result.rowsRequired} ${rowLabel} (${result.places} TE Reihenkapazität)</span></div><dl class="asv-te-planer__details">${details}<div><dt>Belegt</dt><dd>${result.used.toLocaleString("de-DE")} TE</dd></div><div><dt>Mit ${result.reserve.toLocaleString("de-DE")} % Reserve</dt><dd>${result.withReserve.toLocaleString("de-DE")} TE</dd></div><div><dt>Frei im Raster</dt><dd>${(result.places - result.used).toLocaleString("de-DE")} TE</dd></div></dl><p class="asv-te-planer__hinweis">Reine Platzrechnung. Verdrahtungsraum, Verlustleistung, Schutzkonzept, Kurzschlussfestigkeit und die konkrete Ausführung prüft die Elektrofachkraft.</p>${cards}`;
  }

  function shareFields(root) {
    return [
      ...DEVICE_IDS.flatMap((id) => [[`n.${id}`, field(root, `[data-te-anzahl="${id}"]`)], [`t.${id}`, field(root, `[data-te-wert="${id}"]`)]]),
      ["reserve", field(root, "[data-te-reserve]")], ["proreihe", field(root, "[data-te-pro-reihe]")], ["montage", field(root, "[data-te-montage]")], ["ip", field(root, "[data-te-ip]")],
    ];
  }

  function sourceCitation(root) {
    const title = root.closest("section")?.querySelector("h1")?.textContent?.trim() || document.title;
    const canonical = document.querySelector('link[rel="canonical"]')?.href;
    const url = new URL(canonical || location.pathname, location.origin);
    url.hash = "";
    url.search = "";
    const stand = root.dataset.teStand?.trim() || "12.09.2026";
    const date = new Intl.DateTimeFormat("de-DE").format(new Date());
    return `asvendo GmbH: ${title}. ${url.href} (TE-Planer, Stand ${stand}; abgerufen am ${date}). Produktvorschläge beruhen auf den hinterlegten Angaben zu Kapazität, Reihen, Montage und IP je kaufbarer Variante. Orientierungshilfe; Geräteverteilung und Datenblatt vor dem Kauf prüfen.`;
  }

  function copy(root, value, message) {
    const status = field(root, "[data-te-status]");
    const output = field(root, "[data-te-kopierausgabe]");
    output.hidden = false;
    output.value = value;
    output.focus();
    output.select();
    const fallback = () => {
      status.textContent = "Der Text ist markiert und kann jetzt kopiert werden.";
    };
    if (!navigator.clipboard?.writeText) return fallback();
    try {
      Promise.resolve(navigator.clipboard.writeText(value)).then(() => { status.textContent = message; }).catch(fallback);
    } catch {
      fallback();
    }
  }

  function restore(root) {
    if (!location.hash.startsWith(SHARE_PREFIX) || location.hash.length > 4000) return;
    const params = new URLSearchParams(location.hash.slice(SHARE_PREFIX.length));
    const known = new Set(["v", ...shareFields(root).map(([key]) => key)]);
    if (params.get("v") !== "1" || [...params.keys()].some((key) => !known.has(key))) return;
    for (const [key, input] of shareFields(root)) {
      const value = params.get(key);
      if (value !== null && input) input.value = value;
    }
    show(root, collect(root));
    field(root, "[data-te-status]").textContent = "Geteilte Eingaben geladen.";
  }

  function start(root) {
    if (root.dataset.teBereit) return;
    root.dataset.teBereit = "1";
    field(root, "[data-te-berechnen]").addEventListener("click", () => show(root, collect(root)));
    root.addEventListener("change", (event) => { if (event.target.matches("[data-te-anzahl], [data-te-wert], [data-te-reserve], [data-te-pro-reihe], [data-te-montage], [data-te-ip]")) show(root, collect(root)); });
    field(root, "[data-te-link-kopieren]").addEventListener("click", () => {
      const result = collect(root);
      if (result.error) return show(root, result);
      const url = new URL(location.href);
      url.hash = "";
      const params = new URLSearchParams({ v: "1" });
      for (const [key, input] of shareFields(root)) params.set(key, input.value);
      void copy(root, `${url.href}${SHARE_PREFIX}${params.toString()}`, "Eingabelink kopiert.");
    });
    field(root, "[data-te-quelle-kopieren]").addEventListener("click", () => {
      void copy(root, sourceCitation(root), "Quellenangabe kopiert.");
    });
    field(root, "[data-te-stueckliste-kopieren]").addEventListener("click", () => {
      const result = collect(root);
      if (result.error) return show(root, result);
      const lines = result.rows.map((row) => `${row.count} × ${row.title} (${row.units.toLocaleString("de-DE")} TE je Gerät)`).join("\n");
      void copy(root, `Stückliste Verteiler\n${lines}\n\nTE-Angaben vor Bestellung am konkreten Datenblatt prüfen.`, "Stückliste kopiert.");
    });
    field(root, "[data-te-drucken]").addEventListener("click", () => {
      document.body.classList.add("asv-te-planer-druckansicht");
      window.addEventListener("afterprint", () => document.body.classList.remove("asv-te-planer-druckansicht"), { once: true });
      window.print();
    });
    restore(root);
    window.addEventListener("hashchange", () => restore(root));
  }

  const boot = () => document.querySelectorAll("[data-asv-te-planer]").forEach(start);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  document.addEventListener("shopify:section:load", boot);
})();
